import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { setTabbedViewActiveTab } from '../actions/studentProgressActions';
import ContactForm from '../components/ContactUs/ContactForm';
import ContactOptions from '../components/ContactUs/ContactOptions';
import ContactSuccess from '../components/ContactUs/ContactSuccess';
import DataNotAvailable from '../components/StudentProgress/DataNotAvailable';
import DisciplineList from '../components/StudentProgress/DisciplineList';
import {
  APP_VIEW_CONTACT_FORM,
  APP_VIEW_CONTACT_OPTIONS,
  APP_VIEW_CONTACT_SUCCESS,
  APP_VIEW_DEFAULT,
  STUDENT_PROGRESS_TAB_VIEWS,
} from '../util/constants';
import { scrollToTop } from '../util/helpers';
import { PAGE_NAME_SESSION_REPORT } from '../util/pageTitle';
import { AppShell } from '../v2/app-shell/AppShell';
import { usePageHeader } from '../v2/app-shell/usePageHeader';
import { TabNav } from '../v2/common/TabNav';
import { ReportPage } from './ReportPage';
import StudentProgress from './StudentProgress';

const REPORT_TABS = [
  { value: STUDENT_PROGRESS_TAB_VIEWS.PROGRESS_OVERVIEW, label: 'Progress overview' },
  { value: STUDENT_PROGRESS_TAB_VIEWS.SESSION_REPORT, label: 'Session reports' },
];

const StudentProgressAndReport = ({
  studentName,
  startingDate,
  activeTab,
  setActiveTab,
  disciplineFetchError,
  showContactForm,
  showContactOptions,
  showContactSuccess,
  showDefaultView,
  disciplineListLength,
}) => {
  //useSetPageTitle(PAGE_NAME_SESSION_REPORT);
  const { setHeader } = usePageHeader();

  useEffect(() => {
    setHeader({
      label: PAGE_NAME_SESSION_REPORT,
      showIcon: false,
      showBackButton: true,
    });
  }, [setHeader]);

  const [lastActiveTab, setLastActiveTab] = useState(activeTab);
  const isSessionReportViewActive = activeTab === STUDENT_PROGRESS_TAB_VIEWS.SESSION_REPORT;
  if (activeTab !== lastActiveTab) {
    setLastActiveTab(activeTab);
    scrollToTop();
  }

  const renderActiveTabContent = () => {
    if (disciplineFetchError || disciplineListLength <= 0) {
      return null;
    }

    if (isSessionReportViewActive) {
      return <ReportPage />;
    }

    return <StudentProgress />;
  };

  return (
    <AppShell>
      <div className="relative z-0">
        {showDefaultView && (
          <Fragment>
            {!disciplineFetchError && disciplineListLength > 0 && (
              <div className="sticky-top w-full border-b border-grey-2 font-display">
                <div className="container p-10">
                  <div className="relative flex flex-col gap-4 text-grey-6 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <div className="mb-1 font-[Poppins] font-medium">
                        {studentName}&apos;s learning progress, since {startingDate}
                      </div>
                      <DisciplineList />
                    </div>
                    <TabNav
                      tabs={REPORT_TABS}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      ariaLabel="Report view"
                      className="justify-between md:justify-normal"
                    />
                  </div>
                </div>
              </div>
            )}
            {disciplineFetchError && (
              <div className="my-8 py-8">
                <DataNotAvailable
                  firstLine="We are having some trouble loading your report"
                  secondLine="Please try again in a few minutes"
                  showReloadButton
                />
              </div>
            )}
            {!disciplineFetchError && disciplineListLength === 0 && (
              <div className="my-8 py-8">
                <DataNotAvailable
                  firstLine="No Learning Activity available yet"
                  secondLine="Your Learning Activity will become available once a session is completed"
                />
              </div>
            )}
            {renderActiveTabContent()}
          </Fragment>
        )}
        {showContactOptions && <ContactOptions />}
        {showContactForm && <ContactForm />}
        {showContactSuccess && <ContactSuccess />}
      </div>
    </AppShell>
  );
};

StudentProgressAndReport.propTypes = {
  studentName: PropTypes.string,
  startingDate: PropTypes.string,
  setActiveTab: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  disciplineFetchError: PropTypes.bool.isRequired,
  showDefaultView: PropTypes.bool.isRequired,
  showContactOptions: PropTypes.bool.isRequired,
  showContactForm: PropTypes.bool.isRequired,
  showContactSuccess: PropTypes.bool.isRequired,
  disciplineListLength: PropTypes.number.isRequired,
};

StudentProgressAndReport.defaultProps = {
  studentName: '',
  startingDate: '',
};

export default connect(
  (state) => {
    const { appView } = state.ui;
    return {
      studentName: state.report.studentName,
      startingDate: moment(state.report.startingDate).format('MMM YYYY'),
      activeTab: state.report.activeTab,
      disciplineFetchError: state.ui.apiState.studentProgress.discipline.fetchError,
      showDefaultView: appView === APP_VIEW_DEFAULT,
      showContactOptions: appView === APP_VIEW_CONTACT_OPTIONS,
      showContactForm: appView === APP_VIEW_CONTACT_FORM,
      showContactSuccess: appView === APP_VIEW_CONTACT_SUCCESS,
      disciplineListLength: state.studentProgress.disciplineList.length,
    };
  },
  (dispatch) => {
    return {
      setActiveTab: bindActionCreators(setTabbedViewActiveTab, dispatch),
    };
  }
)(StudentProgressAndReport);
